import React from 'react';
import { compose } from 'recompose';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { translator } from 'mage-i18n';
import { Row, Column } from 'mage-components';
import { generateCyElemId, generateCyElemIdWithValue } from 'moet-test-utils/cypress/utils';
import _unescape from 'lodash/unescape';

import { CY_APPLICATION_CONFIRMATION_CONTAINER_IDS } from '../containers/ApplicationConfirmation/constants';
import { withFeatureToggledValidation } from '../containers/withFeatureToggledValidation';
import SummaryColumn from './SummaryColumn';
import YesNoFieldSummary from './YesNoFieldSummary';
import SummarySection from './SummarySection';

import { getEnrichedVatCountries } from '@/selectors/options';

const tPrefix = 'products-fe.containers.application_summary.businessDetailsCard';
const tPrefixFields = `${tPrefix}Fields`;
const tPrefixOptions = 'products-fe.options';

const {
  EMAIL,
  GOODS_CATEGORY,
  ORGANIZATION_ENTITY_TYPE,
  REGISTRATION_ID,
  STORE_NAME,
  VAT_ID,
  WEBSITE,
} = CY_APPLICATION_CONFIRMATION_CONTAINER_IDS;

const BusinessDetailsSummary = ({
  businessDetails,
  country,
  enrichedVatCountries,
  t,
  onEdit = null,
  forcedValidationConfig = {},
}) => {
  const {
    registrationId: { translationKey: tKeyRegistrationId = country } = {},
  } = forcedValidationConfig;

  const translatedOrganizationEntityType = t(
    `${tPrefixOptions}.organization_entity_types.${businessDetails.organizationEntityType}`,
  );

  return (
    <SummarySection title={t(`${tPrefix}.title`)} onEdit={onEdit}>
      <Row>
        <SummaryColumn
          titleKey={`${tPrefixFields}.${country === 'US' ? 'storeName_us' : 'storeName'}`}
          content={_unescape(businessDetails.storeName)}
          {...generateCyElemId(STORE_NAME, window)}
        />
        <SummaryColumn
          titleKey={`${tPrefixFields}.website`}
          content={businessDetails.website}
          {...generateCyElemId(WEBSITE, window)}
        />
        <SummaryColumn
          titleKey={`${tPrefixFields}.email`}
          content={businessDetails.email}
          {...generateCyElemId(EMAIL, window)}
        />
        {country === 'US' &&
          businessDetails.organizationEntityType &&
          translatedOrganizationEntityType && (
            <SummaryColumn
              titleKey={`${tPrefixFields}.organizationEntityType`}
              content={translatedOrganizationEntityType}
              {...generateCyElemIdWithValue(
                ORGANIZATION_ENTITY_TYPE,
                businessDetails.organizationEntityType,
                window,
              )}
            />
          )}
        <Column columns={{ tablet: 6 }}>
          <YesNoFieldSummary
            country={tKeyRegistrationId}
            booleanAnswer={businessDetails.registered}
            titleYesKey={`${tPrefixFields}.registrationId`}
            titleNoKey={`${tPrefixFields}.registered`}
            yesAnswer={businessDetails.registrationId}
            {...generateCyElemIdWithValue(REGISTRATION_ID, businessDetails.registrationId, window)}
          />
        </Column>
        {(!enrichedVatCountries.includes(country) || !businessDetails.registered) && (
          <Column columns={{ tablet: 6 }}>
            <YesNoFieldSummary
              country={country}
              booleanAnswer={businessDetails.subjectToVat}
              titleYesKey={`${tPrefixFields}.vatId`}
              titleNoKey={`${tPrefixFields}.subjectToVat`}
              yesAnswer={businessDetails.vatId}
              {...generateCyElemIdWithValue(VAT_ID, businessDetails.vatId, window)}
            />
          </Column>
        )}
        <SummaryColumn
          titleKey={`${tPrefixFields}.goodsCategory`}
          content={t(`${tPrefixOptions}.goods_categories.${businessDetails.goodsCategory}`)}
          {...generateCyElemIdWithValue(GOODS_CATEGORY, businessDetails.goodsCategory, window)}
        />
      </Row>
    </SummarySection>
  );
};

BusinessDetailsSummary.propTypes = {
  businessDetails: PropTypes.object.isRequired,
  country: PropTypes.string.isRequired,
  enrichedVatCountries: PropTypes.arrayOf(PropTypes.string).isRequired,
  t: PropTypes.func.isRequired,
  forcedValidationConfig: PropTypes.object,
  onEdit: PropTypes.func,
};

export default compose(
  connect(state => ({
    t: translator(state),
    enrichedVatCountries: getEnrichedVatCountries(state),
  })),
  withFeatureToggledValidation('registrationId'),
)(BusinessDetailsSummary);
